'use client';

import React, { useEffect, useMemo, useState } from 'react';

import {
    Alignable,
    Box,
    BoxContent,
    BoxHeader,
    Button,
    ButtonList,
    ButtonListDirectionEnum,
    ButtonVariant,
    Checkbox,
    Icon,
    IconName,
    IconSize,
    Justifiable,
    Popover,
    Price,
    PriceLevel,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Sticker,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyAlign,
    TypographyBold,
    VariantState,
    View,
} from '@bytel/trilogy-react';

import { FundingType } from '@app-types/funding';
import { MobilePlanType } from '@app-types/plan';
import { UserType, UserTypeEnum } from '@app-types/user';

import { InlinePrice } from '@components/inline-price';
import { PromoHomeSticker } from '@components/promo-home-sticker';
import { SmartChangeIcon } from '@components/smart-change';

import { MarketingsState } from '@hooks/use-marketings';

import { setSessionFunding } from '@helpers/funding';
import { getPricesWithPromos } from '@helpers/offer-wall';
import { LockedPlanValue, getBlockedPlans, setPlanLock } from '@helpers/plan-lock';
import { formatPrice } from '@helpers/wording';

import { MarketingType, PlanRange, PlanTypeId } from '@constants/plan';

type OfferCardProps = {
    user?: UserType;
    offer: MobilePlanType;
    openOfferDetails: (offer: MobilePlanType) => void;
    hasPhoneInCart?: boolean;
    isPhoneLoading: boolean;
    onAddToCart: (selectedOffer: MobilePlanType, fundingKey?: string) => Promise<void>;
    isConnected: boolean;
    marketingDiscounts: MarketingsState;
    isSmallPrice: boolean;
    showBlockedPlanZone: boolean;
    hatInLine: boolean;
    selectedFunding?: FundingType;
    customCtaLabel?: string;
    customName?: string;
};

export function OfferCard({
    user,
    offer,
    openOfferDetails,
    hasPhoneInCart,
    isPhoneLoading,
    onAddToCart,
    isConnected,
    marketingDiscounts,
    isSmallPrice,
    showBlockedPlanZone = false,
    hatInLine,
    selectedFunding,
    customCtaLabel,
    customName,
}: Readonly<OfferCardProps>) {
    const productDetails = offer.details;
    const [isLoading, setIsLoading] = useState(false);
    const [isLocked, setIsLocked] = useState<boolean>(false);

    const configurators = useMemo(() => {
        const promoApplicable = [MarketingType.PORTABILITY, MarketingType.MULTILINE];
        if (marketingDiscounts?.FOYER) {
            promoApplicable.push(MarketingType.HOME);
        }
        return promoApplicable;
    }, [marketingDiscounts]);

    const offerPrices = useMemo(
        () =>
            getPricesWithPromos(
                productDetails?.price?.forever || 0,
                productDetails?.discounts || [],
                configurators,
                isConnected,
            ),
        [productDetails?.price?.forever, productDetails?.discounts, configurators, isConnected],
    );

    const homeDiscount = useMemo(
        () => productDetails?.discounts?.find((promo) => promo.marketingTypes?.includes(MarketingType.HOME)),
        [productDetails?.discounts],
    );

    const ctaLabel = useMemo(() => {
        if (customCtaLabel) {
            return customCtaLabel;
        }
        return offer.commercialRange === PlanRange.WITH_MOBILE && !hasPhoneInCart
            ? 'Choisir un téléphone'
            : 'Choisir ce forfait';
    }, [customCtaLabel, offer.isOwned, offer.commercialRange, hasPhoneInCart]);

    const discountsWithoutHome = useMemo(
        () =>
            offer.details?.discounts
                ?.filter((discount) => !discount.marketingTypes.includes(MarketingType.HOME))
                .reduce((acc, discount) => acc + discount.value, 0) || 0,
        [offer.details?.discounts],
    );

    const initialPrice = productDetails?.price.initial - discountsWithoutHome;

    const finalPrice = offerPrices.final;
    const foreverPrice = offerPrices.forever || 0;

    const strikedPrice = useMemo(() => {
        const hasPromoMultiline = productDetails?.discounts?.find((discount) =>
            discount.marketingTypes.includes(MarketingType.MULTILINE),
        );

        let discountedPrice = undefined;

        if (!hasPromoMultiline) {
            discountedPrice = initialPrice;
        } else if (isSmallPrice) {
            discountedPrice = foreverPrice;
        }

        return discountedPrice;
    }, [productDetails?.discounts, initialPrice, isSmallPrice, foreverPrice]);

    useEffect(() => {
        if (offer.entityId) {
            setIsLocked(getBlockedPlans()[offer.entityId] || false);
        }
    }, [offer.entityId]);

    if (!offerPrices) {
        return null;
    }

    const handleAddToCart = async () => {
        setIsLoading(true);
        if (selectedFunding) {
            setSessionFunding(selectedFunding.type);
        }
        await onAddToCart(offer, selectedFunding?.type);
        setIsLoading(false);
    };

    const getPlanName = () => {
        if (customName) {
            return customName;
        }
        return offer?.dataEnvelope;
    };
    const shouldShowAddToCartButton = !(
        user?.user_type === UserTypeEnum.CDC &&
        offer.isOwned &&
        (offer.typeId.includes(PlanTypeId.SOWO) || offer.commercialRange === PlanRange.WITHOUT_MOBILE)
    );

    return (
        <Row>
            <Box
                data-cy={`plan-${offer.gencode}`}
                fullheight
                headerOffset={!(offer?.planFlag || offer.isOwned || !hatInLine)}
            >
                {(offer?.planFlag || offer.isOwned) && (
                    <BoxHeader className='is-justified-center'>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: (offer.isOwned ? 'Mon forfait actuel' : offer?.planFlag)!,
                            }}
                            className='is-flex is-aligned-center'
                        />
                    </BoxHeader>
                )}
                <BoxContent>
                    <Rows gap={2}>
                        <Row narrow>
                            <View flexable justify={Justifiable.SPACE_BETWEEN} align={Alignable.ALIGNED_CENTER}>
                                <Title level={customName ? TitleLevels.THREE : TitleLevels.TWO} marginless>
                                    {getPlanName()}
                                </Title>
                                <Icon
                                    name={offer?.maxConnections === '5g' ? IconName.UI_5G : IconName.UI_4G}
                                    size='medium'
                                    stretched
                                />
                            </View>
                        </Row>
                        <Row narrow className='has-text-left card-details'>
                            <p
                                className='text is-level-3'
                                dangerouslySetInnerHTML={{ __html: offer.detailsDescription || '' }}
                            />
                        </Row>
                        {showBlockedPlanZone && (
                            <Row narrow>
                                {offer.locked === LockedPlanValue.ONE && (
                                    <Checkbox
                                        id={offer.entityId}
                                        label='Forfait bloqué'
                                        value={offer.entityId}
                                        checked={isLocked}
                                        onChange={(value) => {
                                            setIsLocked(!isLocked);
                                            setPlanLock(value.checkboxValue, !isLocked);
                                        }}
                                    />
                                )}
                                {offer.locked === LockedPlanValue.TWO && <Text>- Forfait bloqué</Text>}
                            </Row>
                        )}
                        <Row narrow>
                            <View
                                flexable
                                justify={Justifiable.JUSTIFIED_CENTER}
                                align={Alignable.ALIGNED_CENTER}
                                className='sticker-zone'
                            >
                                {homeDiscount && !homeDiscount.isIncitation && homeDiscount.pennant && (
                                    <PromoHomeSticker small promo={homeDiscount} />
                                )}
                                {isSmallPrice && offerPrices.promoLabel && (
                                    <Sticker variant={VariantState.INFO} small label={offerPrices.promoLabel} />
                                )}
                            </View>
                        </Row>
                        <Row narrow>
                            <View justify={Justifiable.JUSTIFIED_CENTER} align={Alignable.ALIGNED_CENTER} flexable>
                                <Price
                                    amount={finalPrice}
                                    oldAmount={strikedPrice !== finalPrice ? strikedPrice : undefined}
                                    period='mois'
                                    level={PriceLevel.THREE}
                                    data-cy='plan-final-price'
                                />
                            </View>
                        </Row>
                        {finalPrice > 0 && (
                            <Row narrow className='card-obligation'>
                                <Spacer size={SpacerSize.TWO} />

                                {(!homeDiscount || homeDiscount.isIncitation) &&
                                    Boolean(offerPrices.promoLabel) &&
                                    !isSmallPrice && (
                                        <Text
                                            typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                                            level={TextLevels.ONE}
                                            marginless
                                        >
                                            {offerPrices.promoLabel}
                                        </Text>
                                    )}
                                {(offerPrices?.duration ?? 0) > 0 && (
                                    <Text typo={TypographyAlign.TEXT_CENTERED} marginless level={TextLevels.THREE}>
                                        x{offerPrices.duration} mois puis&nbsp;
                                        <InlinePrice level={TextLevels.THREE} amount={foreverPrice} period='mois' />
                                    </Text>
                                )}
                                <View flexable className='is-centered is-vcentered'>
                                    <Text
                                        marginless
                                        typo={TypographyAlign.TEXT_LEFT}
                                        level={TextLevels.THREE}
                                        className='capitalize-first'
                                    >
                                        {offer.obligationLabel}
                                    </Text>
                                    {(offerPrices.duration || 0) > 0 && offerPrices.final !== offerPrices.forever && (
                                        <Popover
                                            trigger={
                                                <Text level={TextLevels.THREE}>
                                                    <Icon size={IconSize.SMALLER} name={IconName.INFOS_CIRCLE} />
                                                </Text>
                                            }
                                        >
                                            {`Prix sans conservation du numéro : ${formatPrice(foreverPrice)}/mois`}
                                        </Popover>
                                    )}
                                </View>
                                <Spacer size={SpacerSize.THREE} />
                            </Row>
                        )}
                        {offer.hasSmartChangeEquivalent && (
                            <Row narrow>
                                <Box flat shadowless>
                                    <BoxContent
                                        backgroundColor={TrilogyColor.MAIN_FADE}
                                        className='p-small is-flex is-centered'
                                    >
                                        <SmartChangeIcon height={24} />
                                    </BoxContent>
                                    <BoxContent className='p-small has-text-centered'>
                                        <Text
                                            level={TextLevels.THREE}
                                            marginless
                                            typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                        >
                                            est disponible avec votre téléphone
                                        </Text>
                                        <Spacer size={SpacerSize.TWO} />
                                        <Text level={TextLevels.THREE} marginless>
                                            <Icon name={IconName.CHECK} size={IconSize.SMALL} />
                                            Changez dès 12 mois si vous voulez
                                        </Text>
                                        <Text level={TextLevels.THREE} marginless>
                                            <Icon name={IconName.CHECK} size={IconSize.SMALL} />
                                            Payez à crédit 0% et sans frais
                                        </Text>
                                    </BoxContent>
                                </Box>
                                <Spacer size={SpacerSize.FOUR} />
                            </Row>
                        )}
                        <Row className='is-content-end'>
                            <ButtonList direction={ButtonListDirectionEnum.COLUMN} className='gap-medium mt-auto'>
                                {shouldShowAddToCartButton ? (
                                    <Button
                                        loading={isLoading || isPhoneLoading}
                                        onClick={handleAddToCart}
                                        type='button'
                                        variant={ButtonVariant.CONVERSION}
                                        fullwidth
                                    >
                                        {ctaLabel}
                                    </Button>
                                ) : undefined}

                                <Button
                                    onClick={() => openOfferDetails(offer)}
                                    type='button'
                                    variant={ButtonVariant.SECONDARY}
                                    fullwidth
                                >
                                    Voir les détails
                                </Button>
                            </ButtonList>
                        </Row>
                        {offer.detailsDescription && (
                            <Row narrow>
                                <Text level={TextLevels.THREE} marginless>
                                    *&nbsp;Liste des destinations dans &quot;Voir les détails&quot;
                                </Text>
                            </Row>
                        )}
                    </Rows>
                </BoxContent>
            </Box>
        </Row>
    );
}
